import React, { useEffect, useState } from 'react';
import PageLayout from '../components/PageLayout';
import SafeIcon from '../common/SafeIcon';
import AudioButton from '../components/AudioButton';
import { getImageUrl } from '../config';
import * as FiIcons from 'react-icons/fi';

const { FiUsers, FiUser, FiCrown, FiLink, FiHeart, FiArrowRight, FiSmile, FiStar } = FiIcons;

const Pronouns = ({ updateProgress, setCurrentSection }) => {
  const [activeTab, setActiveTab] = useState('independent');

  useEffect(() => {
    setCurrentSection('pronouns');
    updateProgress('pronouns');
  }, []);

  const pronounCards = {
    independent: [
      {
        amharic: 'እኔ (ene)',
        english: 'I',
        usage: 'First person singular',
        visual: 'Child pointing to self',
        image: getImageUrl('pointing-self.jpg'),
        audioId: 'ene',
        color: 'bg-blue-50 border-blue-200',
        iconColor: 'text-blue-600',
        relationship: 'self'
      },
      {
        amharic: 'አንተ (ante)',
        english: 'you (masc.)',
        usage: 'Second person masculine',
        visual: 'Casual talk with friend',
        image: getImageUrl('pointing-friend-he.jpg'), // Updated
        audioId: 'ante',
        color: 'bg-green-50 border-green-200',
        iconColor: 'text-green-600',
        relationship: 'casual'
      },
      {
        amharic: 'አንቺ (anchi)',
        english: 'you (fem.)',
        usage: 'Second person feminine',
        visual: 'Girl talking to friend',
        image: getImageUrl('pointing-friend.jpg'),
        audioId: 'anchi',
        color: 'bg-pink-50 border-pink-200',
        iconColor: 'text-pink-600',
        relationship: 'casual'
      },
      {
        amharic: 'እሱ (essu)',
        english: 'he',
        usage: 'Third person masculine',
        visual: 'Pointing to a boy',
        image: getImageUrl('pointing-he.jpg'),
        audioId: 'essu',
        color: 'bg-purple-50 border-purple-200',
        iconColor: 'text-purple-600',
        relationship: 'third-person'
      },
      {
        amharic: 'እሷ (esswa)',
        english: 'she',
        usage: 'Third person feminine',
        visual: 'Pointing to a girl',
        image: getImageUrl('pointing-she.jpg'),
        audioId: 'esswa',
        color: 'bg-orange-50 border-orange-200',
        iconColor: 'text-orange-600',
        relationship: 'third-person'
      },
      {
        amharic: 'እኛ (egna)',
        english: 'we',
        usage: 'First person plural',
        visual: 'Group holding hands',
        image: getImageUrl('group-we.jpg'),
        audioId: 'egna',
        color: 'bg-indigo-50 border-indigo-200',
        iconColor: 'text-indigo-600',
        relationship: 'collective'
      },
      {
        amharic: 'እናንተ (ennante)',
        english: 'you all',
        usage: 'Second person plural',
        visual: 'Talking to a group',
        image: getImageUrl('group-you.jpg'),
        audioId: 'ennante',
        color: 'bg-teal-50 border-teal-200',
        iconColor: 'text-teal-600',
        relationship: 'plural'
      },
      {
        amharic: 'እነሱ (ennesu)',
        english: 'they',
        usage: 'Third person plural',
        visual: 'Group standing together',
        image: getImageUrl('group-they.jpg'),
        audioId: 'ennesu',
        color: 'bg-red-50 border-red-200',
        iconColor: 'text-red-600',
        relationship: 'plural'
      }
    ],
    possessive: [
      {
        example: 'ቤቴ (bete)',
        meaning: 'my house',
        suffix: '-e',
        visual: 'Child holding key',
        image: getImageUrl('house-my-house.jpg'), // Updated
        audioId: 'bete',
        color: 'bg-amber-50 border-amber-200',
        iconColor: 'text-amber-600',
        relationship: 'possession'
      },
      {
        example: 'ቤትህ (betih)',
        meaning: 'your house (masc.)',
        suffix: '-ih',
        visual: 'Pointing to friend\'s house',
        image: getImageUrl('house-generic.jpg'),
        audioId: 'betih',
        color: 'bg-emerald-50 border-emerald-200',
        iconColor: 'text-emerald-600',
        relationship: 'possession'
      },
      {
        example: 'ቤትሽ (betish)',
        meaning: 'your house (fem.)',
        suffix: '-ish',
        visual: 'Pointing to friend\'s house',
        image: getImageUrl('house-generic.jpg'),
        audioId: 'betish',
        color: 'bg-rose-50 border-rose-200',
        iconColor: 'text-rose-600',
        relationship: 'possession'
      },
      {
        example: 'ቤቱ (betu)',
        meaning: 'his house',
        suffix: '-u',
        visual: 'Boy outside his house',
        image: getImageUrl('his-house.jpg'), // Updated
        audioId: 'betu',
        color: 'bg-cyan-50 border-cyan-200',
        iconColor: 'text-cyan-600',
        relationship: 'possession'
      },
      {
        example: 'ቤቷ (betwa)',
        meaning: 'her house',
        suffix: '-wa',
        visual: 'Girl outside her house',
        image: getImageUrl('betwa.jpg'),
        audioId: 'betwa',
        color: 'bg-lime-50 border-lime-200',
        iconColor: 'text-lime-600',
        relationship: 'possession'
      },
      {
        example: 'ቤታችን (betachin)',
        meaning: 'our house',
        suffix: '-achin',
        visual: 'Family at home',
        image: getImageUrl('betachin.jpg'),
        audioId: 'betachin',
        color: 'bg-violet-50 border-violet-200',
        iconColor: 'text-violet-600',
        relationship: 'collective'
      }
    ],
    honorific: [
      {
        form: 'አንተ (ante)',
        context: 'Casual - friends, peers',
        respect: 'Low',
        visual: 'Friends laughing',
        image: getImageUrl('pointing-friend-he.jpg'), // Updated
        audioId: 'ante',
        color: 'bg-gray-50 border-gray-200',
        iconColor: 'text-gray-600',
        interaction: 'casual',
        distance: 'Close',
        posture: 'Relaxed',
        setting: 'Playground'
      },
      {
        form: 'እርስዎ (erswo)',
        context: 'Formal - elders, teachers',
        respect: 'High',
        visual: 'Bowing to elder',
        image: getImageUrl('erswo.jpg'),
        audioId: 'erswo',
        color: 'bg-yellow-50 border-yellow-300',
        iconColor: 'text-yellow-700',
        interaction: 'respectful',
        distance: 'Medium-Far',
        posture: 'Bowed',
        setting: 'Classroom'
      },
      {
        form: 'እኛ (egna)',
        context: 'Humble self-reference',
        respect: 'Cultural',
        visual: 'Gesturing to group',
        image: getImageUrl('group-we.jpg'),
        audioId: 'egna',
        color: 'bg-amber-50 border-amber-200',
        iconColor: 'text-amber-600',
        interaction: 'humble',
        distance: 'Close',
        posture: 'Inclusive',
        setting: 'Community'
      },
      {
        form: 'አባታችን (abatachin)',
        context: 'Religious respect',
        respect: 'Highest',
        visual: 'Prayer position',
        image: getImageUrl('abatachin.jpg'),
        audioId: 'abatachin',
        color: 'bg-purple-50 border-purple-300',
        iconColor: 'text-purple-700',
        interaction: 'reverent',
        distance: 'Far',
        posture: 'Deep bow',
        setting: 'Church'
      },
      {
        form: 'እሳቸው (essachew)',
        context: 'Polite 3rd person',
        respect: 'High',
        visual: 'Talking about leader',
        image: getImageUrl('essachew.jpg'),
        audioId: 'essachew',
        color: 'bg-indigo-50 border-indigo-300',
        iconColor: 'text-indigo-700',
        interaction: 'respectful',
        distance: 'Medium',
        posture: 'Attentive',
        setting: 'Formal'
      },
      {
        form: 'አንቺ (anchi)',
        context: 'Familiar casual',
        respect: 'Low',
        visual: 'Sisters talking',
        image: getImageUrl('pointing-friend.jpg'),
        audioId: 'anchi',
        color: 'bg-pink-50 border-pink-200',
        iconColor: 'text-pink-600',
        interaction: 'familiar',
        distance: 'Very Close',
        posture: 'Relaxed',
        setting: 'Home'
      }
    ]
  };

  const getRelationshipIcon = (relationship) => {
    switch (relationship) {
      case 'self': return FiUser;
      case 'casual': return FiSmile;
      case 'third-person': return FiUser;
      case 'collective': return FiUsers;
      case 'plural': return FiUsers;
      case 'possession': return FiCrown;
      default: return FiUser;
    }
  };

  const getInteractionIcon = (interaction) => {
    switch (interaction) {
      case 'casual': return FiSmile;
      case 'respectful': return FiCrown;
      case 'humble': return FiHeart;
      case 'reverent': return FiStar;
      case 'submissive': return FiHeart;
      case 'familiar': return FiSmile;
      default: return FiUsers;
    }
  };

  const getRespectBadgeColor = (respect) => {
    switch (respect) {
      case 'Highest': return 'bg-purple-100 text-purple-700 border-purple-200';
      case 'Supreme': return 'bg-indigo-100 text-indigo-700 border-indigo-200';
      case 'High': return 'bg-yellow-100 text-yellow-700 border-yellow-200';
      case 'Low': return 'bg-gray-100 text-gray-700 border-gray-200';
      case 'Cultural': return 'bg-amber-100 text-amber-700 border-amber-200';
      default: return 'bg-blue-100 text-blue-700 border-blue-200';
    }
  };

  const TabButton = ({ id, label, icon }) => (
    <button
      onClick={() => setActiveTab(id)}
      className={`relative group flex items-center gap-2 px-6 py-3 rounded-2xl font-bold transition-all duration-300 overflow-hidden ${activeTab === id
          ? 'bg-blue-600 text-white shadow-lg shadow-blue-200 scale-105'
          : 'bg-white text-gray-500 hover:bg-gray-50 hover:text-gray-700 border border-gray-100'
        }`}
    >
      <div className={`absolute inset-0 bg-white/20 translate-y-full group-hover:translate-y-0 transition-transform duration-300 ${activeTab === id ? 'block' : 'hidden'}`}></div>
      <SafeIcon icon={icon} className={`text-lg ${activeTab === id ? 'animate-bounce' : ''}`} />
      <span className="relative z-10">{label}</span>
    </button>
  );

  return (
    <PageLayout
      title="Pronouns & Relationships"
      subtitle="Learn how pronouns in Amharic do more than just replace nouns—they establish social hierarchy and respect."
      icon={FiUsers}
      color="text-blue-700"
      bgColor="bg-blue-50"
      prevLink="/nouns"
      prevLabel="Nouns"
      nextLink="/cultural"
      nextLabel="Cultural Context"
    >
      {/* Tab Navigation */}
      <div className="flex flex-wrap gap-4 mb-10 p-2 bg-gray-50/50 rounded-3xl w-fit">
        <TabButton id="independent" label="Independent" icon={FiUser} />
        <TabButton id="possessive" label="Possessive" icon={FiCrown} />
        <TabButton id="honorific" label="Honorifics" icon={FiLink} />
      </div>

      {/* Visual Card Grid */}
      <div className="space-y-8 min-h-[500px]">
        {activeTab === 'independent' && (
          <div className="fade-in">
            <div className="prose max-w-none mb-8">
              <h3 className="text-2xl font-bold text-gray-800 mb-2">Independent Pronouns</h3>
              <p className="text-gray-500 text-lg">
                Each pronoun represents a specific relationship and social context.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {pronounCards.independent.map((card, idx) => (
                <div
                  key={idx}
                  className={`group relative flex flex-col h-full bg-white rounded-[2rem] border-2 border-b-[6px] ${card.color} p-6 transition-all duration-300 hover:-translate-y-2 hover:shadow-xl cursor-pointer`}
                >
                  {/* Visual Placeholder */}
                  <div className={`relative h-48 mb-4 rounded-3xl overflow-hidden bg-white/60 border-2 border-white/50 shadow-inner flex items-center justify-center group-hover:scale-105 transition-transform duration-500`}>
                    <img src={card.image} alt={card.english} className="w-full h-full object-cover absolute inset-0" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                    <div className="text-center z-10 relative mt-auto mb-4">
                      <p className="text-xs text-white font-bold px-2">{card.visual}</p>
                    </div>
                  </div>

                  {/* Pronoun Content */}
                  <div className="space-y-3 mt-auto">
                    <div className="flex justify-between items-center">
                      <span className="font-amharic text-2xl font-black text-gray-800 tracking-wide">
                        {card.amharic.split(' ')[0]}
                      </span>
                      <AudioButton audioId={card.audioId} text={card.amharic.split(' ')[0]} />
                    </div>
                    <div className="pt-3 border-t border-gray-100/50">
                      <div className={`font-bold text-base mb-1 ${card.iconColor}`}>
                        {card.english}
                      </div>
                      <div className="text-xs text-gray-500 font-medium leading-relaxed">
                        {card.usage}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'possessive' && (
          <div className="fade-in">
            <div className="prose max-w-none mb-8">
              <h3 className="text-2xl font-bold text-gray-800 mb-2">Possessive Pronouns</h3>
              <p className="text-gray-500 text-lg">
                Possessive forms show ownership through suffixes.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {pronounCards.possessive.map((card, idx) => (
                <div
                  key={idx}
                  className={`group relative flex flex-col h-full bg-white rounded-[2rem] border-2 border-b-[6px] ${card.color} p-6 transition-all duration-300 hover:-translate-y-2 hover:shadow-xl`}
                >
                  {/* Visual Placeholder */}
                  <div className="relative h-48 mb-5 rounded-3xl overflow-hidden bg-white/60 border-2 border-white/50 shadow-inner flex items-center justify-center">
                    <img src={card.image} alt={card.meaning} className="w-full h-full object-cover absolute inset-0" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                    {/* Suffix Badge - 3D Pill */}
                    <div className="absolute top-3 right-3 bg-white shadow-md border border-gray-100 rounded-2xl px-3 py-1.5 flex items-center gap-1.5 transform rotate-3 group-hover:rotate-0 transition-transform z-10">
                      <span className="w-1.5 h-1.5 rounded-full bg-purple-500"></span>
                      <span className="text-xs font-bold text-gray-700 font-mono tracking-tight">{card.suffix}</span>
                    </div>
                  </div>

                  {/* Possessive Content */}
                  <div className="space-y-3 mt-auto">
                    <div className="flex justify-between items-center bg-white/50 rounded-2xl p-2 -mx-2">
                      <span className="font-amharic text-2xl font-black text-gray-800 px-2">
                        {card.example.split(' ')[0]}
                      </span>
                      <AudioButton audioId={card.audioId} text={card.example.split(' ')[0]} />
                    </div>
                    <div className="space-y-1 px-1">
                      <div className={`font-bold text-base ${card.iconColor}`}>
                        {card.meaning}
                      </div>
                      <div className="text-xs text-gray-400 font-medium flex items-center gap-1">
                        <SafeIcon icon={FiLink} className="text-xs" /> Suffix: <span className="text-gray-600 bg-white px-1.5 rounded border border-gray-100">{card.suffix}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'honorific' && (
          <div className="fade-in">
            <div className="prose max-w-none mb-8">
              <h3 className="text-2xl font-bold text-gray-800 mb-2">Honorific Levels</h3>
              <p className="text-gray-500 text-lg">
                The choice between casual and formal pronouns establishes social hierarchy.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {pronounCards.honorific.map((card, idx) => (
                <div
                  key={idx}
                  className={`group relative flex flex-col h-full bg-white rounded-[2rem] border-2 border-b-[8px] ${card.color} p-6 transition-all duration-300 hover:-translate-y-2 hover:shadow-xl`}
                >
                  {/* Visual Placeholder */}
                  <div className="relative h-48 mb-6 rounded-3xl overflow-hidden bg-white/60 border-2 border-white/50 shadow-inner flex items-center justify-center">
                    <img src={card.image} alt={card.context} className="w-full h-full object-cover absolute inset-0" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                    {/* Respect Level Badge */}
                    <div className={`absolute top-3 left-3 px-3 py-1.5 rounded-xl border-b-4 text-xs font-bold uppercase tracking-wider shadow-sm z-10 ${getRespectBadgeColor(card.respect)}`}>
                      {card.respect} Respect
                    </div>
                  </div>

                  {/* Honorific Content */}
                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <span className="font-amharic text-3xl font-black text-gray-800">
                        {card.form.split(' ')[0]}
                      </span>
                      <AudioButton audioId={card.audioId} text={card.form.split(' ')[0]} />
                    </div>
                    <div className={`font-bold text-sm uppercase tracking-wide px-3 py-1 rounded-lg w-fit ${card.iconColor.replace('text-', 'bg-').replace('600', '100').replace('700', '100')} ${card.iconColor}`}>
                      {card.context}
                    </div>
                    {/* Social Dynamics Pucks */}
                    <div className="grid grid-cols-2 gap-3 pt-2">
                      <div className="bg-gray-50 rounded-2xl p-3 border border-gray-100 text-center group-hover:bg-white group-hover:shadow-sm transition-all">
                        <span className="block text-[10px] text-gray-400 font-bold uppercase mb-1">Distance</span>
                        <span className="block text-xs font-bold text-gray-700">{card.distance}</span>
                      </div>
                      <div className="bg-gray-50 rounded-2xl p-3 border border-gray-100 text-center group-hover:bg-white group-hover:shadow-sm transition-all">
                        <span className="block text-[10px] text-gray-400 font-bold uppercase mb-1">Posture</span>
                        <span className="block text-xs font-bold text-gray-700">{card.posture}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Cultural Insight Card - 3D Style */}
      <div className="mt-12 group relative bg-white rounded-[2.5rem] p-8 border-2 border-b-[8px] border-blue-200 shadow-xl overflow-hidden hover:-translate-y-1 transition-all duration-300">
        <div className="absolute top-0 right-0 w-64 h-64 bg-blue-50 rounded-full mix-blend-multiply filter blur-3xl opacity-50 translate-x-1/2 -translate-y-1/2"></div>
        <div className="relative z-10 flex flex-col md:flex-row items-start gap-6">
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white shadow-lg shadow-blue-200 rotate-3 group-hover:rotate-6 transition-transform">
            <SafeIcon icon={FiHeart} className="text-3xl" />
          </div>
          <div className="flex-1">
            <h4 className="text-xl font-bold text-gray-900 mb-3">Social Dynamics Through Visual Language</h4>
            <p className="text-gray-600 leading-relaxed text-lg">
              In Ethiopian culture, honorifics are communicated through more than just words—they involve body language, physical distance, posture, and setting. The visual demonstrations above show how children can understand these complex social dynamics through observational learning.
            </p>
          </div>
        </div>
      </div>
    </PageLayout>
  );
};

export default Pronouns;